import type { BillingSession } from "@rosc/contracts";
import { Button } from "@rosc/ui/v32/button";
import { Meter } from "@rosc/ui/v32/meter";
import {
  ArrowUpRight,
  CreditCard,
  Loader2,
  Sparkles,
  TriangleAlert,
  Wallet,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StripeCustomerSessionType } from "@/common/enums/enums";
import { useStripeGetSession } from "@/hooks/stripe/useStripeGetSession";
import { planDisplayName } from "@/lib/plan-name";
import { useOwnUsage } from "@/lib/usage-queries";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import {
  formatPlanDate,
  isFreePlan,
  type MinutesSummary,
  minutesSummary,
  type PlanDateLine,
  planDateLine,
  planStatusKind,
} from "./plan";
import { PlanStatusChip, SettingsCard } from "./SettingsCard";

// The public plans and prices (rosc.ai). Prices are not in the API; the
// checkout page (Stripe pricing table) shows them.
export const PRICING_URL = "https://rosc.ai/#pricing";

type Translate = (key: string, options?: Record<string, unknown>) => string;

// "Free minutes reset on November 20, 2026" / "Renews on …" / "Ends on …".
export const planDateText = (
  t: Translate,
  line: PlanDateLine,
  language: string,
): string | null =>
  line
    ? t(`settings.subscription.v3.date.${line.kind}`, {
        date: formatPlanDate(line.date, language),
      })
    : null;

const shortDate = (date: Date, language: string): string =>
  date.toLocaleDateString(language || "en", { month: "short", day: "numeric" });

// Minutes of this period: left, used of the allowance and a meter on the
// free plan; minutes used on paid plans (as Analytics: no quota shown).
export const MinutesMeter = ({
  summary,
  free,
  language,
}: {
  summary: MinutesSummary;
  free: boolean;
  language: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const period =
    summary.periodStart && summary.periodEnd
      ? t("settings.subscription.v3.minutes.period", {
          start: shortDate(summary.periodStart, language),
          end: shortDate(summary.periodEnd, language),
        })
      : null;
  if (!free)
    return (
      <div data-testid="subscription-minutes">
        <p className="font-v3-display text-[22px] font-semibold leading-7 tabular-nums">
          {t("settings.subscription.v3.minutes.used", {
            count: summary.usedMinutes,
          })}
        </p>
        {period && (
          <p className="mt-0.5 text-[13px] text-v3-text-3">{period}</p>
        )}
      </div>
    );
  const empty = summary.leftMinutes <= 0;
  const low = !empty && summary.usedShare >= 0.9;
  return (
    <div data-testid="subscription-minutes">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p
          className={cn(
            "font-v3-display text-[22px] font-semibold leading-7 tabular-nums",
            empty && "text-v3-rec-text",
          )}
        >
          {t("v3.shell.minutesLeft", { count: summary.leftMinutes })}
        </p>
        <p className="text-[13px] tabular-nums text-v3-text-3">
          {t("settings.subscription.v3.minutes.usedOf", {
            used: summary.usedMinutes,
            total: summary.allowanceMinutes + summary.extraMinutes,
          })}
        </p>
      </div>
      <Meter
        className="mt-2.5"
        label={t("settings.subscription.v3.minutes.meterLabel")}
        tone={empty ? "rec" : low ? "warn" : "ink"}
        value={summary.usedShare}
      />
      <p className="mt-2 text-[13px] text-v3-text-3">
        {[
          period,
          summary.extraMinutes > 0
            ? t("settings.subscription.v3.minutes.extra", {
                count: summary.extraMinutes,
              })
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  );
};

// Upgrade (Stripe pricing table, for accounts without a paid plan) or
// Manage subscription (Stripe billing portal, for paid plans): the server
// decides which session it returns.
export const BillingActions = ({
  session,
  loading,
  error,
  onRetry,
  size = "default",
}: {
  session: BillingSession | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  size?: "default" | "sheet";
}): JSX.Element => {
  const { t } = useTranslation();
  const sheet = size === "sheet";
  const buttonClass = cn(sheet && "max-sm:h-12 max-sm:w-full");
  if (error)
    return (
      <div
        className="flex flex-col items-start gap-2 sm:flex-row sm:items-center"
        role="alert"
      >
        <p className="flex-1 text-[14px] text-v3-text-2">{error}</p>
        <Button className={buttonClass} variant="secondary" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      </div>
    );
  if (!session || loading)
    return (
      <Button
        aria-busy
        className={buttonClass}
        disabled
        variant="primary"
        data-testid="billing-loading"
      >
        <Loader2 aria-hidden className="animate-spin" />
        {t("common.loading")}
      </Button>
    );
  if (session.type === StripeCustomerSessionType.BILLING_SESSION)
    return (
      <div className={cn("flex flex-wrap gap-2", sheet && "max-sm:flex-col")}>
        <Button
          className={buttonClass}
          data-testid="billing-manage"
          variant="primary"
          onClick={() => window.open(session.url, "_blank", "noopener")}
        >
          {t("settings.subscription.v3.manage")}
          <ArrowUpRight aria-hidden />
        </Button>
      </div>
    );
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        sheet && "max-sm:flex-col max-sm:items-stretch",
      )}
    >
      <Button
        className={buttonClass}
        data-testid="billing-upgrade"
        variant="primary"
        onClick={() =>
          window.open(session.url, "_blank", "noopener,noreferrer")
        }
      >
        <Sparkles aria-hidden />
        {t("settings.subscription.v3.upgrade")}
      </Button>
      <Button asChild className={buttonClass} variant="ghost">
        <a href={PRICING_URL} rel="noopener noreferrer" target="_blank">
          {t("settings.subscription.v3.comparePlans")}
          <ArrowUpRight aria-hidden />
        </a>
      </Button>
    </div>
  );
};

// Settings › Subscription in the v3.2 app: the plan and its status, this
// period's minutes, when it renews or ends, and the existing Stripe flows
// (pricing table to upgrade, billing portal to manage).
export const SubscriptionPanel = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const userData = useAuthStore((state) => state.userData);
  const usage = useOwnUsage();
  const billing = useStripeGetSession();
  const { fetch: fetchSession } = billing;
  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  const free = isFreePlan(userData?.planName);
  const status = planStatusKind(userData?.planStatus);
  const dateText = planDateText(
    t as Translate,
    planDateLine(userData),
    language,
  );
  const paymentProblem =
    status === "pastDue" || status === "unpaid" || status === "incomplete";
  const upgrade =
    billing.result?.type === StripeCustomerSessionType.CUSTOMER_SESSION ||
    (!billing.result && free);

  return (
    <div
      className="flex flex-col gap-5 sm:gap-4"
      data-testid="subscription-panel"
    >
      <SettingsCard
        aside={<PlanStatusChip status={userData?.planStatus} />}
        description={dateText}
        icon={<CreditCard aria-hidden />}
        labelledBy="subscription-plan"
        testId="subscription-plan"
        title={
          planDisplayName(userData?.planName) ||
          t("settings.subscription.v3.noPlan")
        }
      >
        {paymentProblem && (
          <p
            className="mb-4 flex gap-2.5 rounded-xl bg-v3-warn-tint px-3.5 py-2.5 text-[14px] leading-snug text-v3-warn-text"
            role="status"
          >
            <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
            {t("settings.subscription.v3.paymentProblem")}
          </p>
        )}
        {usage.error ? (
          <div className="flex flex-wrap items-center gap-2" role="alert">
            <p className="flex-1 text-[14px] text-v3-text-2">
              {t("settings.subscription.v3.minutes.unavailable")}
            </p>
            <Button variant="secondary" onClick={() => void usage.refresh()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : usage.data ? (
          <MinutesMeter
            free={free}
            language={language}
            summary={minutesSummary(usage.data)}
          />
        ) : (
          <div aria-hidden className="space-y-2.5">
            <div className="h-7 w-32 animate-pulse rounded-md bg-v3-surface-2" />
            <div className="h-2 animate-pulse rounded-full bg-v3-surface-2" />
          </div>
        )}
      </SettingsCard>
      <SettingsCard
        description={
          upgrade
            ? t("settings.subscription.v3.upgradeDescription")
            : t("settings.subscription.v3.manageDescription")
        }
        icon={upgrade ? <Sparkles aria-hidden /> : <Wallet aria-hidden />}
        labelledBy="subscription-billing"
        testId="subscription-billing"
        title={
          upgrade
            ? t("settings.subscription.v3.upgradeTitle")
            : t("settings.subscription.v3.manageTitle")
        }
      >
        <BillingActions
          error={billing.error}
          loading={billing.loading}
          session={billing.result}
          onRetry={() => void fetchSession()}
        />
      </SettingsCard>
    </div>
  );
};
